// src/pages/Shop.js
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';
import { useProducts } from '../context/ProductContext';
import { useTheme } from '../components/ThemeProvider';
import { useContent } from '../context/ContentContext';
import ProductCard from '../components/shop/ProductCard';
import Button from '../components/common/Button';
import SectionLoader from '../components/common/SectionLoader';

function Shop() {
  const { search } = useLocation();
  const { formatPrice } = useCurrency();
  const { products, loading: productsLoading, error } = useProducts();
  const { primaryColor } = useTheme();
  const { content, loading: contentLoading } = useContent();
  const navigate = useNavigate();
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [category, setCategory] = useState('all');
  const [filters, setFilters] = useState({
    sizes: [],
    colors: [],
    materials: [],
    genres: [],
    formats: [],
    propertyTypes: [],
    bedrooms: [],
    styles: [],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const productsPerPage = 10;

  const pageRef = useRef(null);

  const uniqueCategories = [
    'all',
    ...new Set(products.map((p) => p.category)),
    'others',
  ].filter((cat, idx, arr) => arr.indexOf(cat) === idx);

  const hasCategory = (cat) => products.some((p) => p.category === cat);

  const uniqueSizes = [
    ...new Set(
      products
        .filter((p) => ['clothing', 'shoes'].includes(p.category))
        .flatMap((p) => p.sizes || [])
    ),
  ].sort();

  const uniqueColors = [
    ...new Set(
      products
        .filter((p) => ['clothing', 'shoes'].includes(p.category))
        .flatMap((p) => p.colors || [])
    ),
  ].sort();

  const uniqueMaterials = [
    ...new Set(
      products
        .filter((p) => ['clothing', 'shoes'].includes(p.category))
        .map((p) => p.material)
        .filter(Boolean)
    ),
  ].sort();

  const uniqueGenres = [
    ...new Set(
      products
        .filter((p) => p.category === 'books')
        .map((p) => p.genre)
        .filter(Boolean)
    ),
  ].sort();

  const uniqueFormats = [
    ...new Set(
      products
        .filter((p) => p.category === 'books')
        .flatMap((p) => p.format || [])
        .filter(Boolean)
    ),
  ].sort();

  const uniquePropertyTypes = [
    ...new Set(
      products
        .filter((p) => p.category === 'real_estate')
        .map((p) => p.type)
        .filter(Boolean)
    ),
  ].sort();

  const uniqueBedrooms = [
    ...new Set(
      products
        .filter((p) => p.category === 'real_estate')
        .map((p) => p.bedrooms)
        .filter(Boolean)
    ),
  ].sort((a, b) => a - b);

  const uniqueStyles = [
    ...new Set(
      products
        .filter((p) => p.category === 'shoes')
        .map((p) => p.style)
        .filter(Boolean)
    ),
  ].sort();

  useEffect(() => {
    if (!productsLoading && !error) {
      setFilteredProducts(products);
    }
  }, [products, productsLoading, error]);

  useEffect(() => {
    const query = new URLSearchParams(search);
    const querySearch = query.get('search')?.toLowerCase() || '';
    setSearchTerm(querySearch);

    let result = products;

    if (category !== 'all') {
      if (category === 'others') {
        result = result.filter(
          (p) => !['clothing', 'books', 'shoes', 'real_estate', 'cars'].includes(p.category)
        );
      } else {
        result = result.filter((p) => p.category === category);
      }
    }

    if (querySearch) {
      result = result.filter((p) => {
        const nameMatch = p.name.toLowerCase().includes(querySearch);
        const descMatch = p.description?.toLowerCase().includes(querySearch);
        const authorMatch =
          p.category === 'books' && p.author?.toLowerCase().includes(querySearch);
        const isbnMatch = p.category === 'books' && p.isbn?.includes(querySearch);
        return nameMatch || descMatch || authorMatch || isbnMatch;
      });
    }

    if (filters.sizes.length > 0) {
      result = result.filter(
        (p) =>
          ['clothing', 'shoes'].includes(p.category) &&
          p.sizes?.some((size) => filters.sizes.includes(size))
      );
    }
    if (filters.colors.length > 0) {
      result = result.filter(
        (p) =>
          ['clothing', 'shoes'].includes(p.category) &&
          p.colors?.some((color) => filters.colors.includes(color))
      );
    }
    if (filters.materials.length > 0) {
      result = result.filter(
        (p) =>
          ['clothing', 'shoes'].includes(p.category) &&
          filters.materials.includes(p.material)
      );
    }
    if (filters.genres.length > 0) {
      result = result.filter(
        (p) => p.category === 'books' && filters.genres.includes(p.genre)
      );
    }
    if (filters.formats.length > 0) {
      result = result.filter(
        (p) =>
          p.category === 'books' && p.format?.some((f) => filters.formats.includes(f))
      );
    }
    if (filters.propertyTypes.length > 0) {
      result = result.filter(
        (p) =>
          p.category === 'real_estate' && filters.propertyTypes.includes(p.type)
      );
    }
    if (filters.bedrooms.length > 0) {
      result = result.filter(
        (p) =>
          p.category === 'real_estate' && filters.bedrooms.includes(p.bedrooms)
      );
    }
    if (filters.styles.length > 0) {
      result = result.filter(
        (p) => p.category === 'shoes' && filters.styles.includes(p.style)
      );
    }

    setFilteredProducts(result);
    setCurrentPage(1);
  }, [search, filters, category, products]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const currentProducts = filteredProducts.slice(
    startIndex,
    startIndex + productsPerPage
  );

  const toggleFilter = (filterType, value) => {
    setFilters((prev) => {
      const current = prev[filterType];
      if (current.includes(value)) {
        return { ...prev, [filterType]: current.filter((v) => v !== value) };
      }
      return { ...prev, [filterType]: [...current, value] };
    });
  };

  const clearFilters = () => {
    setFilters({
      sizes: [],
      colors: [],
      materials: [],
      genres: [],
      formats: [],
      propertyTypes: [],
      bedrooms: [],
      styles: [],
    });
    setCategory('all');
    setSearchTerm('');
    navigate('/shop');
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (error) return <div className="text-center py-24 text-3xl font-montserrat text-red-600">{error}</div>;

  return (
    <div className="bg-cream min-h-screen">
      {/* Hero Welcome Banner */}
      <div className="relative bg-gradient-to-br from-primary/5 via-cream to-cream py-16 sm:py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(255,255,255,0.9),transparent_60%)]" />
        </div>

        {contentLoading ? (
          <div className="relative z-10 py-20">
            <SectionLoader message="Preparing your welcome..." />
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-playfair-display font-semibold text-primary mb-6 tracking-tight leading-tight">
              Welcome to {content?.settings?.brand_name || 'Our Space'}
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl font-montserrat text-secondary max-w-4xl mx-auto mb-10 lg:mb-12 leading-relaxed">
              Explore everything we’ve put together for you — made with care and attention to detail.
            </p>

            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <Button
                onClick={() => {
                  setCategory('all');
                  const gridElement = document.querySelector('[role="region"][aria-live="polite"]');
                  if (gridElement) {
                    gridElement.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-primary text-cream px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl rounded-full hover:bg-primary/90 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-primary/40"
              >
                See Everything
              </Button>

            </div>
          </div>
        )}
      </div>

      {/* Main Shop Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Active Filters Bar */}
        {(category !== 'all' || Object.values(filters).some(arr => arr.length > 0) || searchTerm) && (
          <div className="mb-8 flex flex-wrap items-center gap-3 text-sm sm:text-base font-montserrat text-secondary bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <span className="font-medium opacity-80">Current view:</span>
            {category !== 'all' && (
              <span className="bg-primary/10 text-primary px-4 py-1.5 rounded-full font-medium">
                {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
              </span>
            )}
            {searchTerm && (
              <span className="bg-primary/10 text-primary px-4 py-1.5 rounded-full font-medium">
                "{searchTerm}"
              </span>
            )}
            {Object.entries(filters).flatMap(([key, vals]) =>
              vals.map(val => (
                <span
                  key={`${key}-${val}`}
                  className="bg-primary/10 text-primary px-4 py-1.5 rounded-full font-medium flex items-center gap-2"
                >
                  {val}
                  <button
                    onClick={() => toggleFilter(key, val)}
                    className="text-primary/70 hover:text-primary text-xs"
                  >
                    ×
                  </button>
                </span>
              ))
            )}
            <button
              onClick={clearFilters}
              className="text-primary hover:text-primary/80 underline text-sm ml-3"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Filter Sidebar */}
          <div className="lg:col-span-1">
            {productsLoading ? (
              <SectionLoader message="Loading filters..." />
            ) : (
              <div className="bg-white p-6 lg:p-8 rounded-2xl shadow-2xl border border-gray-100/70 sticky top-8 transition-all duration-300">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl sm:text-3xl font-playfair-display font-semibold text-primary">
                    Filter & Explore
                  </h2>
                  <Button
                    onClick={clearFilters}
                    className="text-sm font-montserrat text-cream hover:text-primary/80 hover:underline transition-colors px-3 py-1.5"
                    aria-label="Clear all filters"
                  >
                    Clear All
                  </Button>
                </div>

                {/* Search */}
                <div className="mb-8">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      navigate(`/shop?search=${encodeURIComponent(e.target.value)}`);
                    }}
                    className="w-full p-4 border border-gray-300 rounded-xl text-lg font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all shadow-sm hover:shadow-md"
                    style={{ '--primary': primaryColor }}
                    aria-label="Search"
                  />
                </div>

                {/* Category Tabs */}
                <div className="mb-10">
                  <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                    Categories
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    {uniqueCategories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setCategory(cat)}
                        className={`px-5 py-2.5 rounded-full font-montserrat text-sm transition-all duration-300 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                          category === cat
                            ? 'bg-primary text-cream shadow-lg scale-105'
                            : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                        }`}
                        style={{ '--primary': primaryColor }}
                        aria-label={`Show ${cat === 'all' ? 'all' : cat}`}
                      >
                        {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filters */}
                {(hasCategory('clothing') || hasCategory('shoes')) &&
                  (['all', 'clothing', 'shoes'].includes(category) ||
                    category === 'others') && (
                    <>
                      {uniqueSizes.length > 0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                            Sizes
                          </h3>
                          <div className="flex flex-wrap gap-2.5">
                            {uniqueSizes.map((size) => (
                              <button
                                key={size}
                                onClick={() => toggleFilter('sizes', size)}
                                className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                  filters.sizes.includes(size)
                                    ? 'bg-primary text-cream shadow-lg'
                                    : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                }`}
                                style={{ '--primary': primaryColor }}
                                aria-label={`Size ${size}`}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {uniqueColors.length > 0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                            Colors
                          </h3>
                          <div className="flex flex-wrap gap-2.5">
                            {uniqueColors.map((color) => (
                              <button
                                key={color}
                                onClick={() => toggleFilter('colors', color)}
                                className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                  filters.colors.includes(color)
                                    ? 'bg-primary text-cream shadow-lg'
                                    : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                }`}
                                style={{ '--primary': primaryColor }}
                                aria-label={`Color ${color}`}
                              >
                                {color}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {uniqueMaterials.length > 0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                            Materials
                          </h3>
                          <div className="flex flex-wrap gap-2.5">
                            {uniqueMaterials.map((material) => (
                              <button
                                key={material}
                                onClick={() => toggleFilter('materials', material)}
                                className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                  filters.materials.includes(material)
                                    ? 'bg-primary text-cream shadow-lg'
                                    : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                }`}
                                style={{ '--primary': primaryColor }}
                                aria-label={`Material ${material}`}
                              >
                                {material}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {hasCategory('shoes') &&
                        uniqueStyles.length > 0 &&
                        (category === 'shoes' || category === 'all' || category === 'others') && (
                          <div className="mb-8">
                            <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                              Styles
                            </h3>
                            <div className="flex flex-wrap gap-2.5">
                              {uniqueStyles.map((style) => (
                                <button
                                  key={style}
                                  onClick={() => toggleFilter('styles', style)}
                                  className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                    filters.styles.includes(style)
                                      ? 'bg-primary text-cream shadow-lg'
                                      : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                  }`}
                                  style={{ '--primary': primaryColor }}
                                  aria-label={`Style ${style}`}
                                >
                                  {style}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                    </>
                  )}

                {hasCategory('books') &&
                  (['all', 'books'].includes(category) || category === 'others') && (
                    <>
                      {uniqueGenres.length > 0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                            Genres
                          </h3>
                          <div className="flex flex-wrap gap-2.5">
                            {uniqueGenres.map((genre) => (
                              <button
                                key={genre}
                                onClick={() => toggleFilter('genres', genre)}
                                className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                  filters.genres.includes(genre)
                                    ? 'bg-primary text-cream shadow-lg'
                                    : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                }`}
                                style={{ '--primary': primaryColor }}
                                aria-label={`Genre ${genre}`}
                              >
                                {genre}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {uniqueFormats.length > 0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                            Formats
                          </h3>
                          <div className="flex flex-wrap gap-2.5">
                            {uniqueFormats.map((format) => (
                              <button
                                key={format}
                                onClick={() => toggleFilter('formats', format)}
                                className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                  filters.formats.includes(format)
                                    ? 'bg-primary text-cream shadow-lg'
                                    : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                }`}
                                style={{ '--primary': primaryColor }}
                                aria-label={`Format ${format}`}
                              >
                                {format}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                {hasCategory('real_estate') &&
                  (['all', 'real_estate'].includes(category) || category === 'others') && (
                    <>
                      {uniquePropertyTypes.length > 0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                            Property Type
                          </h3>
                          <div className="flex flex-wrap gap-2.5">
                            {uniquePropertyTypes.map((type) => (
                              <button
                                key={type}
                                onClick={() => toggleFilter('propertyTypes', type)}
                                className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                  filters.propertyTypes.includes(type)
                                    ? 'bg-primary text-cream shadow-lg'
                                    : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                }`}
                                style={{ '--primary': primaryColor }}
                                aria-label={`Type ${type}`}
                              >
                                {type}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {uniqueBedrooms.length > 0 && (
                        <div className="mb-8">
                          <h3 className="text-lg font-montserrat font-semibold text-primary mb-4">
                            Bedrooms
                          </h3>
                          <div className="flex flex-wrap gap-2.5">
                            {uniqueBedrooms.map((bedroom) => (
                              <button
                                key={bedroom}
                                onClick={() => toggleFilter('bedrooms', bedroom)}
                                className={`px-5 py-2.5 rounded-full text-sm font-montserrat transition-all duration-300 shadow-sm hover:shadow-md ${
                                  filters.bedrooms.includes(bedroom)
                                    ? 'bg-primary text-cream shadow-lg'
                                    : 'bg-white text-primary border border-gray-200 hover:border-primary hover:bg-primary/5'
                                }`}
                                style={{ '--primary': primaryColor }}
                                aria-label={`${bedroom} bedrooms`}
                              >
                                {bedroom}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
              </div>
            )}
          </div>

          {/* Product Grid Section */}
          <div className="lg:col-span-3" role="region" aria-live="polite">
            {productsLoading ? (
              <SectionLoader message="Loading products..." />
            ) : currentProducts.length === 0 ? (
              <div className="text-center py-24 lg:py-32 bg-white rounded-2xl shadow-2xl border border-gray-100/70 p-12">
                <div className="max-w-lg mx-auto">
                  <h2 className="text-4xl font-playfair-display font-semibold text-primary mb-6">
                    Nothing here yet
                  </h2>
                  <p className="text-xl font-montserrat text-secondary mb-10 leading-relaxed">
                    We couldn't find any items matching your current view. Try something else?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-5 justify-center">
                    <Button
                      onClick={clearFilters}
                      className="bg-primary text-cream px-10 py-5 text-xl rounded-full hover:bg-primary/90 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
                      style={{ '--primary': primaryColor }}
                    >
                      Clear Filters
                    </Button>
                    <Button
                      onClick={() => setCategory('all')}
                      className="border-2 border-primary text-cream px-10 py-5 text-xl rounded-full hover:bg-primary/5 transition-all duration-300 shadow-xl hover:shadow-2xl"
                    >
                      See Everything
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Featured Teaser */}
                {/* {filteredProducts.length > 6 && (
                  <div className="mb-16 lg:mb-20">
                    <h2 className="text-3xl sm:text-4xl font-playfair-display font-semibold text-primary mb-8 text-center tracking-tight">
                      Featured This Week
                    </h2>
                    {productsLoading ? (
                      <SectionLoader message="Loading featured items..." />
                    ) : (
                      <div className="overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <div className="flex gap-6 min-w-max">
                          {filteredProducts.slice(0, 10).map((product) => (
                            <div
                              key={product.id}
                              className="w-72 sm:w-80 flex-shrink-0 transform hover:-translate-y-3 transition-all duration-400"
                            >
                              <ProductCard product={product} formatPrice={formatPrice} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )} */}

                {/* Main Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
                  {currentProducts.map((product) => (
                    <div
                      key={product.id}
                      className="transform hover:-translate-y-3 transition-all duration-400"
                    >
                      <ProductCard product={product} formatPrice={formatPrice} />
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Pagination */}
            {totalPages > 1 && !productsLoading && (
              <div className="flex justify-center items-center gap-4 sm:gap-6 mt-16 lg:mt-20 flex-wrap">
                <Button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-8 py-5 bg-white border-2 border-primary text-cream rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/5 transition-all duration-300 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/40 text-lg"
                  style={{ '--primary': primaryColor }}
                  aria-label="Previous page"
                >
                  ← Previous
                </Button>

                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  const isCurrent = currentPage === pageNum;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-6 py-5 rounded-full font-montserrat text-lg transition-all duration-300 min-w-[64px] shadow-lg hover:shadow-xl ${
                        isCurrent
                          ? 'bg-primary text-cream shadow-2xl scale-110'
                          : 'bg-white border border-gray-200 text-primary hover:border-primary hover:bg-primary/5'
                      }`}
                      style={{ '--primary': primaryColor }}
                      aria-label={`Page ${pageNum}`}
                      aria-current={isCurrent ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <Button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-8 py-5 bg-white border-2 border-primary text-cream rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/5 transition-all duration-300 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/40 text-lg"
                  style={{ '--primary': primaryColor }}
                  aria-label="Next page"
                >
                  Next →
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Shop;